import type {
  KeyboardEvent,
  MouseEvent,
  PropsWithChildren,
  ReactNode,
} from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { ICS204_SECTION_TITLE, ICS204_SURFACE } from './assignmentCardStyles';

/**
 * Numbering follows the boxes on the ICS 204-CG form. Boxes 1–2 are read-only
 * on the card (incident + future OP); 3–8 are editable.
 */
export const ICS204_BOX_LABELS = {
  incidentName: '1. Incident Name:',
  operationalPeriod: '2. Operational Period:',
  assignedLocation: '3. Assigned Location:',
  personnel: '4. Operations or Intelligence/Inspections Personnel:',
  resourcesAssigned: '5. Resources Assigned:',
  workAssignments: '6. Work Assignments:',
  specialInstructions: '7. Special Instructions:',
  communications: '8. Communications:',
} as const;

type CardSectionProps = {
  boxLabel: string;
  testId: string;
  /** Product-tour spotlight. */
  tour?: string;
  className?: string;
  /** Optional controls aligned to the right of the section title (e.g. Edit). */
  headerAction?: ReactNode;
  /** When set, the section is clickable to enter edit (editable draft). */
  onSectionActivate?: () => void;
  /** Marks the section currently in edit mode (for outside-click scoping). */
  isEditing?: boolean;
};

/**
 * Shared numbered section chrome for view and edit modes. Seated on jet black
 * so shadcn Card matches the ICS-204 surface.
 */
export const Ics204CardSection = ({
  boxLabel,
  testId,
  tour,
  className = 'tw:mt-3',
  headerAction,
  onSectionActivate,
  isEditing = false,
  children,
}: PropsWithChildren<CardSectionProps>) => {
  const interactive = !!onSectionActivate && !isEditing;

  const activate = (event: MouseEvent | KeyboardEvent) => {
    if (!interactive) return;
    const target = event.target as HTMLElement | null;
    const current = event.currentTarget as HTMLElement | null;
    // Portaled dialogs/menus still bubble through the React tree to this Card.
    // Ignore those so e.g. roster checkboxes don't enter section edit and unmount
    // the dialog. Sections rendered *inside* a dialog must still activate — the
    // dialog content is their ancestor, not foreign UI.
    const portaledUi = target?.closest(
      [
        '[data-slot="dialog-content"]',
        '[data-slot="alert-dialog-content"]',
        '[data-slot="select-content"]',
        '[data-slot="dropdown-menu-content"]',
        '[data-slot="popover-content"]',
        '[data-slot="tooltip-content"]',
      ].join(', ')
    );
    if (
      portaledUi &&
      current instanceof Node &&
      !portaledUi.contains(current)
    ) {
      return;
    }
    const blockedBy = target?.closest(
      'button, a, input, textarea, select, [role="button"], [role="checkbox"], [data-slot="checkbox"]'
    );
    // Nested controls only (pencil etc.). Section root is not role=button.
    if (blockedBy && blockedBy !== current) {
      return;
    }
    onSectionActivate?.();
  };

  return (
    <Card
      size="sm"
      className={cn(
        ICS204_SURFACE,
        'tw:rounded tw:border tw:border-[var(--border-primary)] tw:text-foreground tw:shadow-none tw:ring-0 tw:gap-0 tw:py-0 tw:[--card-spacing:--spacing(3)]',
        interactive && 'tw:cursor-pointer tw:hover:border-[var(--neutral-500)]',
        className
      )}
      data-testid={testId}
      data-tour={tour}
      data-ics204-section-editing={isEditing ? 'true' : undefined}
      onClick={activate}
      onKeyDown={(event) => {
        if (!interactive) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          activate(event);
        }
      }}
      tabIndex={interactive ? 0 : undefined}
    >
      <CardHeader className="tw:px-3 tw:pt-3 tw:pb-0">
        <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
          <CardTitle
            className={cn(ICS204_SECTION_TITLE, 'tw:mb-0 tw:text-foreground')}
          >
            {boxLabel}
          </CardTitle>
          {headerAction ? (
            <div
              className="tw:shrink-0"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              {headerAction}
            </div>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="tw:px-3 tw:pt-3 tw:pb-3 tw:text-foreground">
        {children}
      </CardContent>
    </Card>
  );
};
